import { Link } from "react-router-dom";

import type { LotItem } from "../api/types";
import { itemHref } from "../search/nav";

/** A lot's contents in one cell: the first few names, then how many more, with
 *  the whole list on hover.
 *
 *  A lot can hold a dozen items and the all-time packages page shows every lot
 *  ever seen, so an un-truncated list makes rows of wildly different heights and
 *  a table nobody can scan. The `title` is what keeps truncation from being
 *  lossy — the full list is always one hover away.
 *
 *  One component rather than a copy per table: LotsTable's own comment records
 *  two copies of that table drifting before anyone noticed, and this is the
 *  cell most likely to drift next. */
const SHOWN = 3;

export function LotItems({ items }: { items: LotItem[] }) {
  const shown = items.slice(0, SHOWN);
  const rest = items.length - shown.length;

  return (
    <ul
      className="lot-items"
      title={items.length > SHOWN ? items.map((i) => i.listed).join(", ") : undefined}
    >
      {/* Keyed by id AND position: offers_unique spans qty and price, so one
          lot can legally hold the same item twice at different counts, and a
          bare id would collide. */}
      {shown.map((item, index) => (
        <li key={`${item.id}-${index}`}>
          {/* Linked by the item, labelled by the listing: itemHref puts the
              name in ?name= as the item page's heading, and that page is about
              the plain item however this lot happened to list it. */}
          <Link to={itemHref(item.id, item.name)}>{item.listed}</Link>
        </li>
      ))}
      {/* A list item, not a sibling of the list. .lot-items is a flex row whose
          `li + li::before` draws the separator dots, so this way "+2 more" sits
          in the row with a dot before it like every other entry. Outside the
          <ul> it was both invalid markup — a block-level list inside phrasing
          content — and visually orphaned onto its own line. */}
      {rest > 0 ? <li className="dim">+{rest} more</li> : null}
    </ul>
  );
}
